import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '@/hooks/useAuthSession';
import { storePaymentBackup, recoverSubscriptionFromPayment } from '@/utils/PaymentHelper';
import { getStoredReferralCode } from '@/utils/AffiliateUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trackSubscriptionEvent } from '@/utils/subscriptionAnalytics';

declare global {
  interface Window {
    Razorpay?: any;
  }
}

interface CheckoutButtonProps {
  planId: string;
  planName?: string;
  billingCycle?: 'monthly' | 'yearly';
  children?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

export const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  planId,
  planName,
  billingCycle = 'monthly',
  children = "Subscribe",
  variant = "default",
  className = ""
}) => {
  const [loading, setLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [discount, setDiscount] = useState<{amount: number, original: number, final: number} | null>(null);
  const [showPromoInput, setShowPromoInput] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthSession();

  useEffect(() => {
    if (window.Razorpay) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => {
      console.error("Failed to load Razorpay script");
      toast.error("Payment system failed to load. Please try again later.");
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handlePaymentSuccess = async (paymentId: string, orderId: string, paymentData: any) => {
    try {
      const backupData = {
        paymentId,
        orderId,
        planId,
        billingCycle,
        timestamp: new Date().toISOString(),
        amount: paymentData.amount,
        currency: paymentData.currency,
        planName: paymentData.plan_name || 'Subscription'
      };

      storePaymentBackup(backupData);
      toast.success("Payment successful!");

      try {
        await recoverSubscriptionFromPayment(paymentId, orderId);
      } catch (err) {
        console.error("Subscription sync failed. Redirecting anyway.");
      }

      navigate(`/subscription/success?payment_id=${paymentId}&order_id=${orderId}&plan_id=${planId}&billing_cycle=${billingCycle}`);
    } catch (err) {
      console.error("Error after payment:", err);
      navigate(`/subscription/success?payment_id=${paymentId}&order_id=${orderId}`);
    }
  };

  const handlePaymentError = (error: any) => {
    console.error("Payment failed:", error);
    toast.error(`Payment failed: ${error.description || error.message || 'Unknown error'}`);
    navigate('/subscription/cancel');
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoMessage('');
      setDiscount(null);
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: { 
          code: promoCode,
          planId,
          billingCycle
        }
      });

      if (error) throw error;
      
      if (data.valid) {
        setDiscount({
          amount: data.discount_amount,
          original: data.original_amount,
          final: data.final_amount
        });
        setPromoMessage(`${data.discount_amount > 0 ? 'Promo code applied!' : ''}`);
        toast.success('Promo code applied!');
      } else {
        setPromoMessage(data.message || 'Invalid promo code');
        setDiscount(null);
        toast.error(data.message || 'Invalid promo code');
      }
    } catch (error: any) {
      console.error("Promo code validation error:", error);
      setPromoMessage('Failed to validate code');
      setDiscount(null);
      toast.error('Failed to validate promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionError = (error: any) => {
    console.error("Subscription error:", error);
    
    // Handle different types of subscription errors
    if (error?.error_type === "trial_with_active_subscription") {
      trackSubscriptionEvent('downgrade_attempted', {
        previous_plan: error.current_plan,
        new_plan: 'Trial'
      });
      
      toast.error("Cannot subscribe to Trial plan", {
        description: "Trial plans are only available for users without active subscriptions. Please wait until your current subscription expires.",
        duration: 6000
      });
    } 
    else if (error?.error_type === "downgrade_not_allowed") {
      trackSubscriptionEvent('downgrade_attempted', {
        previous_plan: error.current_plan,
        new_plan: error.requested_plan
      });
      
      toast.error(`Cannot downgrade subscription`, {
        description: `You cannot downgrade from ${error.current_plan} to ${error.requested_plan}. Please wait until your current subscription expires.`,
        duration: 5000
      });
    }
    else if (error?.error_type === "trial_already_used") {
      toast.error("Trial already used", {
        description: "You have already used a trial subscription previously.",
        duration: 4000
      });
    }
    else if (error?.error_type === "invalid_billing_cycle") {
      toast.error("Invalid billing cycle", {
        description: "Trial plans are only available on a monthly basis.",
        duration: 4000
      });
    }
    else {
      toast.error(`Checkout failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to subscribe");
      navigate('/auth');
      return;
    }

    if (!planId) {
      toast.error("No plan selected");
      return;
    }

    if (!isScriptLoaded) {
      toast.error("Payment system is still loading.");
      return;
    }

    setLoading(true);
    try {
      const shortPlanId = planId.split('-')[0];
      const shortUserId = Math.random().toString(36).substring(2, 8);
      const referralCode = getStoredReferralCode();

      const timezoneOffsetMinutes = new Date().getTimezoneOffset();
      const timezoneString = `${timezoneOffsetMinutes <= 0 ? '+' : '-'}${String(Math.floor(Math.abs(timezoneOffsetMinutes / 60))).padStart(2, '0')}:${String(Math.abs(timezoneOffsetMinutes % 60)).padStart(2, '0')}`;

      const { data, error } = await supabase.functions.invoke('create-razorpay-checkout', {
        body: {
          planId,
          mode: billingCycle,
          receiptPrefix: `rcpt_${shortPlanId}_${shortUserId}`,
          timezoneOffset: timezoneString,
          referralCode,
          promoCode: promoCode.trim() || undefined
        }
      });

      if (error) {
        throw error;
      }

      // Check if the response indicates an error
      if (!data?.success && data?.error) {
        handleSubscriptionError(data);
        return;
      }

      if (!data?.plan_name) throw new Error("Missing order or plan name");
      
      console.log(`Razorpay Environment: ${data.environment}`);
      
      // Check if this is a free order (100% discount)
      if (data.is_free_order) {
        console.log("Processing free order with 100% discount");
        toast.success("100% discount applied - Processing subscription");
        
        // Handle free order success directly without opening Razorpay
        handlePaymentSuccess(
          `free_${Date.now()}`, // Generate a unique payment ID 
          data.order_id,
          {
            plan_name: data.plan_name,
            amount: 0,
            currency: data.currency,
            billingCycle,
            referral_code: referralCode || '',
            promo_code: promoCode || '',
            is_free_order: true
          }
        );
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Edaptica",
        description: "Subscription Payment (Incl. 18% GST)",
        order_id: data.order_id,
        prefill: {
          name: data.prefill.name,
          email: data.prefill.email
        },
        notes: {
          plan_id: data.plan_id,
          billing_cycle: billingCycle,
          plan_name: data.plan_name,
          base_amount: data.gst_breakdown?.base_amount,
          gst_amount: data.gst_breakdown?.gst_amount,
          gst_rate: data.gst_breakdown?.gst_rate,
          timezone_offset: timezoneString,
          referral_code: referralCode || '',
          promo_code: promoCode || '',
          discount_amount: data.discount_info?.discount_amount || 0
        },
        theme: {
          color: "#6366F1"
        },
        handler: (response: any) => {
          handlePaymentSuccess(response.razorpay_payment_id, response.razorpay_order_id, {
            plan_name: data.plan_name,
            amount: data.amount,
            currency: data.currency,
            billingCycle,
            referral_code: referralCode || '',
            promo_code: promoCode || ''
          });
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", handlePaymentError);
      razorpay.open();
    } catch (error: any) {
      console.error("Checkout error:", error);
      if (error.message?.includes("auth") || error.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate('/auth');
      } else {
        toast.error(`Checkout failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const isTrial = planName?.toLowerCase() === 'trial';

  return (
    <div className="flex flex-col space-y-2 w-full">
      {showPromoInput && (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            className="px-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={loading}
          />
          <Button 
            size="sm"
            variant="outline"
            onClick={applyPromoCode}
            disabled={loading || !promoCode.trim()}
          >
            Apply
          </Button>
        </div>
      )}
      
      {promoMessage && (
        <div className={`text-xs ${discount ? 'text-green-600' : 'text-red-500'}`}>
          {promoMessage}
        </div>
      )}
      
      {discount && discount.amount > 0 && (
        <div className="text-xs text-green-600">
          Discount: ₹{discount.amount.toFixed(2)} (Original: ₹{(discount.original/100).toFixed(2)}, Final: ₹{(discount.final/100).toFixed(2)})
        </div>
      )}
      
      <div className="flex items-center space-x-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-full">
                <Button
                  onClick={handleCheckout}
                  variant={variant}
                  className={className}
                  disabled={loading || !isScriptLoaded}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : children}
                </Button>
              </span>
            </TooltipTrigger>
            {isTrial && (
              <TooltipContent className="max-w-xs">
                <p>Trial plans are only available for new users without active subscriptions.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        
        <Button
          variant="ghost" 
          size="sm"
          type="button"
          onClick={() => setShowPromoInput(!showPromoInput)}
          className="text-xs underline hover:text-indigo-600"
        >
          {showPromoInput ? "Hide promo code" : "Have a promo code?"}
        </Button>
      </div>
    </div>
  );
};
